import React, { useMemo } from "react";
import moment from "moment";
import { FiInbox } from "react-icons/fi";
import { FaArrowUp } from "react-icons/fa";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import { getExpenseTypeName } from "../../../utils/ExpenseConverter";
import { formatDateTime } from "../../../utils/dateTime";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatExpenseDate = (row) => {
  const unix = Number(row?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (row?.f_date) return formatDateTime(row.f_date);
  return "—";
};

const expenseTypeLabel = (expense) =>
  getExpenseTypeName(expense) || "Expense";

const getAccountName = (expense) =>
  expense?.accountId?.account_name || expense?.account_name || "";

const buildSubtitle = (expense) => {
  const parts = [];
  const type = expenseTypeLabel(expense);
  if (type) parts.push(type);
  if (expense?.desc?.trim()) parts.push(expense.desc.trim());
  return parts.join(" · ") || "Expense";
};

const ReportExpenseCard = ({ expense, runningTotal }) => {
  const amount = Number(expense?.expense || 0);
  const subtitle = buildSubtitle(expense);
  const account = getAccountName(expense);

  return (
    <article className="rounded-2xl bg-card border border-line shadow-soft overflow-hidden font-quicksand">
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-dangerSoft">
          <FaArrowUp className="text-danger text-base" aria-hidden />
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          {amount > 0 && (
            <p className="text-xl font-extrabold tabular-nums leading-none text-danger">
              Rs {fmtRs(amount)}
              <span className="text-xs font-semibold ml-2 text-danger">DR</span>
            </p>
          )}
          <p className="text-sm text-ink mt-2 leading-snug line-clamp-3">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="mx-4 border-t border-line" />

      <div className="px-4 py-3 flex items-center justify-between gap-3 text-sm text-muted">
        <span className="shrink-0">{formatExpenseDate(expense)}</span>
        <span className="tabular-nums font-medium text-right truncate">
          {account ? (
            <>Account: {account}</>
          ) : (
            <>Total: Rs {fmtRs(runningTotal)}</>
          )}
        </span>
      </div>
    </article>
  );
};

const ReportExpenseCardList = ({
  expenses = [],
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  searchText,
  setSearchText,
  emptyMessage = "No expenses found for this date range.",
  paginate = true,
  showSearch = true,
}) => {
  const activeExpenses = expenses.filter((row) => row.isDeleted !== true);

  const filtered = activeExpenses.filter((row) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      String(row.desc || "").toLowerCase().includes(q) ||
      expenseTypeLabel(row).toLowerCase().includes(q) ||
      getAccountName(row).toLowerCase().includes(q) ||
      String(row.f_date || "").includes(q)
    );
  });

  const sortedFiltered = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const dateDiff = Number(a.date || 0) - Number(b.date || 0);
        if (dateDiff !== 0) return dateDiff;
        return String(a._id).localeCompare(String(b._id));
      }),
    [filtered]
  );

  const runningTotals = useMemo(() => {
    let total = 0;
    const map = new Map();
    sortedFiltered.forEach((row) => {
      total += Number(row.expense || 0);
      map.set(String(row._id), total);
    });
    return map;
  }, [sortedFiltered]);

  const pageRows = paginate
    ? sortedFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedFiltered;

  if (!activeExpenses.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-[#155f60]/10 flex items-center justify-center mb-4">
          <FiInbox className="text-[#155f60]" size={28} />
        </div>
        <p className="font-bold text-ink text-lg">No expenses</p>
        <p className="text-sm text-muted mt-1 text-center max-w-xs">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-3">
      {showSearch && setSearchText && (
        <AuthInput
          Value={searchText}
          setValue={setSearchText}
          placeholder="Search description, type or account…"
        />
      )}

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-10 text-sm rounded-xl border border-dashed border-line bg-subtle/60">
          No matches for your search.
        </div>
      ) : (
        <div className="space-y-3 rounded-2xl bg-[#F5F5F5] p-3 font-quicksand">
          {pageRows.map((expense) => (
            <ReportExpenseCard
              key={expense._id || expense.id}
              expense={expense}
              runningTotal={runningTotals.get(String(expense._id))}
            />
          ))}
        </div>
      )}

      {paginate && onPageChange && onRowsPerPageChange && (
        <CustomPagination
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          RowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </div>
  );
};

export default ReportExpenseCardList;
